import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BrandTag from "../shared/primitives/BrandTag";
import userHasRole from "../../shared/utils/authUtils";
import { editorRole, csmRole, csmMaintenanceRole } from "../../shared/constants";
import useStoreCameraTree from "../shared/useStoreCameraTree";
import useInferenceMachineCache from "../shared/useInferenceMachineCache";
import useForeignKeys from "../shared/useForeignKeys";
import { selectedShape, storeShape, treeNodeShape } from "../shared/propShapes";
import { computeStatusMap, rollupCount, summarizeStatus } from "../shared/diff";
import CaptureDeviceForm from "./forms/CaptureDeviceForm";
import BulkEditCameraForm from "./forms/BulkEditCameraForm";
import BulkEditDeviceForm from "./forms/BulkEditDeviceForm";
import BulkEditViewForm from "./forms/BulkEditViewForm";
import CameraForm from "./forms/CameraForm";
import ViewForm from "./forms/ViewForm";
import {
  entityKindFromId,
  isTemp,
  tempPublicId,
  MAX_VIEWS_PER_CAMERA,
  activeViewCount,
  isLastActiveView,
} from "./treeReducer";
import { filterTree, isFilterActive, collectMachineOptions, collectSegmentOptions, EMPTY_FILTERS } from "./treeFilter";
import TreeFilterBar from "./TreeFilterBar";

const NODE_PADDING = 12;
const SELECTED_BG = "rgba(99, 102, 241, 0.25)";
const HOVER_BG = "rgba(255, 255, 255, 0.04)";
// Cap the (currently expanded) tree to ~20 rows and scroll past that, so the tree
// never grows tall enough to push the right-hand detail/edit panel off screen.
const MAX_VISIBLE_TREE_ROWS = 20;
const TREE_ROW_HEIGHT = 31; // approximate rendered height of one node row, in px
const MAX_TREE_HEIGHT = MAX_VISIBLE_TREE_ROWS * TREE_ROW_HEIGHT;

function statusBorderColor(status) {
  if (status === "created") return "#17a2b8";
  if (status === "modified") return "#ffc107";
  if (status === "deleted") return "#dc3545";
  if (status === "invalid") return "#dc3545";
  return "transparent";
}

function findDevice(tree, id) {
  return tree.find((d) => d.public_id === id) || null;
}
function findCamera(tree, id) {
  return tree.flatMap((d) => d.cameras || []).find((c) => c.public_id === id) || null;
}
function findView(tree, id) {
  return tree.flatMap((d) => (d.cameras || []).flatMap((c) => c.views || [])).find((v) => v.public_id === id) || null;
}

// Human label for an entity, for messages that must name one out of a bulk save.
function describeEntity(tree, publicId) {
  const kind = entityKindFromId(publicId);
  if (kind === "device") {
    const device = findDevice(tree, publicId);
    return device ? device.name || "(unnamed device)" : publicId;
  }
  if (kind === "camera") {
    const camera = findCamera(tree, publicId);
    return camera ? `Camera ${camera.slot_number}` : publicId;
  }
  if (kind === "view") {
    const view = findView(tree, publicId);
    return view ? `View ${view.view_number}${view.view_name ? ` · ${view.view_name}` : ""}` : publicId;
  }
  return publicId;
}

// Flatten one kind out of the tree, in tree order.
function collectOfKind(tree, kind) {
  if (kind === "device") return tree;
  if (kind === "camera") return tree.flatMap((d) => d.cameras || []);
  if (kind === "view") return tree.flatMap((d) => (d.cameras || []).flatMap((c) => c.views || []));
  return [];
}

function IconButton({ title, onClick, children, danger, disabled }) {
  const [hover, setHover] = useState(false);
  const active = hover && !disabled;
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick();
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: active ? (danger ? "rgba(220,53,69,0.25)" : "rgba(255,255,255,0.1)") : "transparent",
        color: disabled ? "#555" : active && danger ? "#ff7a8a" : "#bbb",
        border: "none",
        borderRadius: 3,
        padding: "1px 6px",
        fontSize: "0.9rem",
        lineHeight: 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

IconButton.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  danger: PropTypes.bool,
  disabled: PropTypes.bool,
};

IconButton.defaultProps = { danger: false, disabled: false };

function ActiveDot({ on }) {
  return (
    <span
      title={on ? "Active" : "Inactive"}
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: on ? "#28a745" : "#6c757d",
      }}
    />
  );
}

ActiveDot.propTypes = { on: PropTypes.bool.isRequired };

function MetaChip({ icon, value, title }) {
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: "0.72rem",
        color: "#bcc1c8",
        background: "rgba(255,255,255,0.06)",
        padding: "1px 6px",
        borderRadius: 3,
      }}
    >
      <span style={{ opacity: 0.8 }}>{icon}</span>
      <span>{value}</span>
    </span>
  );
}

MetaChip.propTypes = {
  icon: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  title: PropTypes.string,
};

MetaChip.defaultProps = { title: undefined };

// The entities a bulk state actually reaches: same kind, still selected, not on
// their way out. Module-level so setBulk updaters can derive it from `prev` alone.
function effectiveSelection(bulkState, tree) {
  if (!bulkState.kind) return [];
  return collectOfKind(tree, bulkState.kind).filter(
    (entity) => bulkState.ids.has(entity.public_id) && !entity.localDeleted
  );
}

// Bulk selection is ephemeral UI state (cleared on leaving the mode), unlike the
// reducer's draft — hence local state in TreePrototype rather than a reducer slice.
const bulkStateShape = PropTypes.shape({
  active: PropTypes.bool.isRequired,
  kind: PropTypes.oneOf(["device", "camera", "view"]),
  ids: PropTypes.instanceOf(Set).isRequired,
});

function TreeNode({
  node,
  depth,
  selectedId,
  expanded,
  statusMap,
  lastErrorId,
  canCreate,
  bulk,
  onToggleBulkSelect,
  onSelect,
  onToggle,
  onAction,
}) {
  const isSelected = selectedId === node.id;
  const isExpanded = expanded.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const status = statusMap.get(node.id) || "pristine";
  const isInvalid = lastErrorId === node.id;
  const effective = isInvalid ? "invalid" : status;
  const rollup = node.rollupCount || 0;
  const [hover, setHover] = useState(false);

  const handleRowClick = () => {
    onSelect(node);
    if (hasChildren) onToggle(node.id);
  };

  const rowStyle = {
    cursor: "pointer",
    padding: "5px 6px",
    paddingLeft: NODE_PADDING + depth * 16,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
    borderLeft: `3px solid ${statusBorderColor(effective)}`,
    background: isSelected ? SELECTED_BG : hover ? HOVER_BG : "transparent",
    textDecoration: status === "deleted" ? "line-through" : "none",
    opacity: status === "deleted" ? 0.55 : 1,
  };

  return (
    <>
      <div
        style={rowStyle}
        onClick={handleRowClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {bulk.active && (
          <input
            type="checkbox"
            data-testid={`select-${node.id}`}
            aria-label={`Select ${node.label}`}
            checked={bulk.ids.has(node.id)}
            // The kind lock: the first checked node fixes what a bulk edit acts on,
            // so a field can never be ambiguous about which entities it reaches.
            // A not-yet-saved node is excluded: it has no server counterpart, so it
            // has no "modified" state and the per-field revert has nothing to restore.
            // A node marked for deletion stays checkable on purpose — disabling it
            // would make the selection impossible to empty, pinning the kind lock.
            disabled={(bulk.kind !== null && bulk.kind !== node.kind) || isTemp(node.id)}
            onChange={() => onToggleBulkSelect(node)}
            // The row click selects and expands; checking must do neither.
            onClick={(event) => event.stopPropagation()}
            style={{ margin: 0, flex: "0 0 auto" }}
          />
        )}
        <span style={{ display: "inline-block", width: 14, color: "#999", userSelect: "none", textAlign: "center" }}>
          {hasChildren ? (isExpanded ? "▾" : "▸") : ""}
        </span>
        <span style={{ marginRight: 4 }}>{node.icon}</span>
        <span style={{ fontWeight: depth === 0 ? 600 : 400 }}>{node.label}</span>
        {node.badge}
        {node.meta}
        {status === "created" && (
          <Badge color="info" style={{ fontSize: "0.6rem" }}>
            new
          </Badge>
        )}
        {status === "modified" && (
          <Badge color="warning" style={{ fontSize: "0.6rem" }}>
            modified
          </Badge>
        )}
        {status === "deleted" && (
          <Badge color="danger" style={{ fontSize: "0.6rem" }}>
            delete
          </Badge>
        )}
        {isInvalid && <span title="Validation error">⚠️</span>}
        {status === "pristine" && rollup > 0 && (
          <Badge color="secondary" style={{ fontSize: "0.6rem" }} title={`${rollup} pending change(s) inside`}>
            {rollup}
          </Badge>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ display: "inline-flex", gap: 2, opacity: hover || isSelected ? 1 : 0.35 }}>
          {canCreate && node.kind !== "view" && (
            <IconButton
              title={
                node.kind === "camera" && node.viewsFull
                  ? `A camera can have at most ${MAX_VIEWS_PER_CAMERA} views`
                  : node.kind === "device"
                  ? "Add camera"
                  : "Add view"
              }
              disabled={node.kind === "camera" && node.viewsFull}
              onClick={() => onAction(node, "add")}
            >
              <i className="fas fa-plus" />
            </IconButton>
          )}
          {canCreate && (
            <IconButton
              title={
                node.kind === "view" && node.viewsFull
                  ? `A camera can have at most ${MAX_VIEWS_PER_CAMERA} views`
                  : "Duplicate"
              }
              disabled={node.kind === "view" && node.viewsFull}
              onClick={() => onAction(node, "duplicate")}
            >
              <i className="fas fa-copy" />
            </IconButton>
          )}
          <IconButton
            title={
              node.kind === "view" && node.lastActiveView ? "A camera must keep at least one active view" : "Delete"
            }
            danger
            disabled={node.kind === "view" && node.lastActiveView}
            onClick={() => onAction(node, "delete")}
          >
            <i className="fas fa-trash-alt" />
          </IconButton>
        </span>
      </div>
      {isExpanded &&
        hasChildren &&
        node.children.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            expanded={expanded}
            statusMap={statusMap}
            lastErrorId={lastErrorId}
            canCreate={canCreate}
            bulk={bulk}
            onToggleBulkSelect={onToggleBulkSelect}
            onSelect={onSelect}
            onToggle={onToggle}
            onAction={onAction}
          />
        ))}
    </>
  );
}

TreeNode.propTypes = {
  node: treeNodeShape.isRequired,
  depth: PropTypes.number.isRequired,
  selectedId: PropTypes.string,
  expanded: PropTypes.instanceOf(Set).isRequired,
  statusMap: PropTypes.instanceOf(Map).isRequired,
  lastErrorId: PropTypes.string,
  /** Whether the user may create nodes — the `editorRole` gate the legacy recorders carried. */
  canCreate: PropTypes.bool.isRequired,
  bulk: bulkStateShape.isRequired,
  onToggleBulkSelect: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
};

TreeNode.defaultProps = { selectedId: null, lastErrorId: null };

function buildTreeNodes(tree, statusMap, machineCache, bodyById, bagById, viewCountByCamera) {
  return tree.map((device) => ({
    kind: "device",
    id: device.public_id,
    icon: "🖥️",
    label: device.name || "(unnamed device)",
    badge: <BrandTag brand={device.brand} />,
    meta: null,
    rollupCount: rollupCount(statusMap, device),
    children: (device.cameras || []).map((camera) => {
      const machineName = camera.inference_machine != null ? machineCache[camera.inference_machine] : null;
      // Cap state is derived from the FULL tree (not the filtered one) so an active
      // filter that hides some views can't make an at-cap camera look addable.
      const viewsFull = (viewCountByCamera.get(camera.public_id) || 0) >= MAX_VIEWS_PER_CAMERA;
      return {
        kind: "camera",
        id: camera.public_id,
        viewsFull,
        icon: camera.is_active === false ? "💤" : "📷",
        label: `Camera ${camera.slot_number}`,
        badge: null,
        meta: (
          <>
            {machineName && <MetaChip icon="💻" value={machineName} title="Inference machine" />}
            <ActiveDot on={camera.is_active !== false} />
          </>
        ),
        rollupCount: rollupCount(statusMap, camera),
        children: (camera.views || []).map((view) => {
          const bodyLvl = view.body_sensitivity != null ? bodyById.get(view.body_sensitivity) : null;
          const bagLvl = view.bag_sensitivity != null ? bagById.get(view.bag_sensitivity) : null;
          const polygonCount = Array.isArray(view.view_area) ? view.view_area.length : 0;
          return {
            kind: "view",
            id: view.public_id,
            // Mirror the parent camera's cap state so the view's own duplicate
            // action can be disabled when the camera is already full.
            viewsFull,
            // Last active view of its camera: delete is barred (≥1 active view rule).
            lastActiveView: isLastActiveView(camera, view.public_id),
            icon: "🎯",
            label: `View ${view.view_number}${view.view_name ? ` · ${view.view_name}` : ""}`,
            badge: null,
            meta: (
              <>
                {polygonCount > 0 && (
                  <span
                    title={polygonCount === 1 ? "Has a polygon" : `${polygonCount} polygons`}
                    style={{ color: "#a5b4fc", fontSize: "0.78rem" }}
                  >
                    <i className="fas fa-draw-polygon" />
                    {polygonCount > 1 && <span style={{ marginLeft: 2 }}>×{polygonCount}</span>}
                  </span>
                )}
                {bodyLvl != null && <MetaChip icon="🧍" value={bodyLvl} title="Body sensitivity level" />}
                {bagLvl != null && <MetaChip icon="👜" value={bagLvl} title="Bag sensitivity level" />}
                <ActiveDot on={view.is_active !== false} />
              </>
            ),
            rollupCount: 0,
            children: [],
          };
        }),
      };
    }),
  }));
}

/**
 * Same "DVR interface" entry point as the legacy VideoRecorderList dropdown, scoped to
 * one device: the server list is narrowed to the machines this device's cameras run on
 * (by hostname, via machineCache), falling back to every server when none match. Each
 * item deep-links to /remotecontrol, whose existing flow (cloud → update-dvr, on-prem →
 * connect-dvr-interface) does the rest — no new command plumbing here.
 */
function DvrInterfaceDropdown({ servers, device, machineCache, storeId }) {
  const [open, setOpen] = useState(false);
  if (!userHasRole(editorRole)) return null;
  const status = servers?.status || null;
  const deviceHostnames = new Set(
    (device.cameras || [])
      .map((camera) => (camera.inference_machine != null ? machineCache[camera.inference_machine] : null))
      .filter((hostname) => hostname && !String(hostname).startsWith("#"))
  );
  // Legacy VideoRecorderList renders every entry and hides the label with DisplayWhen,
  // which leaves blank menu items — filter the non-actionable entries out instead.
  const canUseServer = userHasRole(csmMaintenanceRole) || !userHasRole(csmRole);
  const allEntries = status ? Object.entries(status).filter(([, info]) => info?.id && canUseServer) : [];
  const deviceEntries = allEntries.filter(([hostname]) => deviceHostnames.has(hostname));
  const entries = deviceEntries.length > 0 ? deviceEntries : allEntries;
  return (
    <Dropdown isOpen={open} toggle={() => setOpen((v) => !v)} className="mb-3">
      <DropdownToggle caret size="sm">
        DVR interface
      </DropdownToggle>
      <DropdownMenu>
        {!status ? (
          <DropdownItem disabled>Loading...</DropdownItem>
        ) : entries.length === 0 ? (
          <DropdownItem disabled>No server available</DropdownItem>
        ) : (
          entries.map(([hostname, serverInfo]) => {
            const command = serverInfo.is_cloud ? "update-dvr" : "connect-dvr-interface";
            // `store` is what Remote Control prefills the DVR forms from; omitted when the store
            // is unknown, in which case those forms render unprefilled.
            const storeParam = storeId != null ? `&store=${storeId}` : "";
            return (
              <DropdownItem
                key={hostname}
                onClick={() => {
                  window.location.href = `/remotecontrol?server=${serverInfo.id}&command=${command}${storeParam}`;
                }}
              >
                {hostname}
              </DropdownItem>
            );
          })
        )}
      </DropdownMenu>
    </Dropdown>
  );
}

const serversShape = PropTypes.shape({
  status: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      is_cloud: PropTypes.bool,
    })
  ),
});

const machineCacheShape = PropTypes.objectOf(PropTypes.string);

DvrInterfaceDropdown.propTypes = {
  servers: serversShape,
  device: PropTypes.shape({
    cameras: PropTypes.arrayOf(
      PropTypes.shape({ inference_machine: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })
    ),
  }).isRequired,
  machineCache: machineCacheShape.isRequired,
  storeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

DvrInterfaceDropdown.defaultProps = { servers: null, storeId: null };

function DetailPanel({
  selected,
  tree,
  store,
  foreignKeys,
  dispatch,
  lastError,
  canCreate,
  servers,
  machineCache,
  bulkSelection,
}) {
  // Two or more entities of one kind selected: edit them together. The bulk forms
  // write into the same draft as the single-entity ones, so the tree-wide Save All
  // remains the only commit point — no Apply step in between.
  if (bulkSelection && bulkSelection.entities.length >= 2) {
    const { kind, entities, serverEntities } = bulkSelection;
    return (
      <Card>
        <CardBody>
          {kind === "device" && (
            <BulkEditDeviceForm devices={entities} serverDevices={serverEntities} store={store} dispatch={dispatch} />
          )}
          {kind === "camera" && (
            <BulkEditCameraForm cameras={entities} serverCameras={serverEntities} dispatch={dispatch} />
          )}
          {kind === "view" && (
            <BulkEditViewForm
              views={entities}
              serverViews={serverEntities}
              foreignKeys={foreignKeys}
              dispatch={dispatch}
            />
          )}
        </CardBody>
      </Card>
    );
  }
  // A selection of one has no shared-value question to answer, and offering a
  // "varies" affordance for a single entity would be noise — so it falls back to
  // the single-entity form, which is also what the checkbox alone should open.
  //
  // An explicit row click wins over that fallback: otherwise, with one box checked,
  // clicking any other row did nothing visible and unchecking was the only way out.
  // Checking a box sets `selected` to that node, so the common case still resolves
  // to the checked entity.
  const single =
    !selected.id && bulkSelection && bulkSelection.entities.length === 1
      ? { kind: bulkSelection.kind, id: bulkSelection.entities[0].public_id }
      : selected;
  if (!single.id) {
    return (
      <Card body>
        <em className="text-muted">Select a node on the left to see its fields.</em>
      </Card>
    );
  }
  const fieldError = lastError && lastError.publicId === single.id ? lastError : null;
  if (single.kind === "device") {
    const device = findDevice(tree, single.id);
    if (!device) return <Alert color="warning">Device not found (deleted?).</Alert>;
    return (
      <Card>
        <CardBody>
          {fieldError && (
            <Alert color="danger" className="py-2 mb-2" style={{ fontSize: "0.85rem" }}>
              <strong>{fieldError.field || fieldError.code}</strong>: {fieldError.message}
            </Alert>
          )}
          <DvrInterfaceDropdown
            servers={servers}
            device={device}
            machineCache={machineCache}
            storeId={store?.id ?? null}
          />
          <CaptureDeviceForm
            device={device}
            brands={foreignKeys.brands}
            store={store}
            dispatch={dispatch}
            canCreate={canCreate}
          />
        </CardBody>
      </Card>
    );
  }
  if (single.kind === "camera") {
    const camera = findCamera(tree, single.id);
    if (!camera) return <Alert color="warning">Camera not found (deleted?).</Alert>;
    return (
      <Card>
        <CardBody>
          {fieldError && (
            <Alert color="danger" className="py-2 mb-2" style={{ fontSize: "0.85rem" }}>
              <strong>{fieldError.field || fieldError.code}</strong>: {fieldError.message}
            </Alert>
          )}
          <CameraForm camera={camera} foreignKeys={foreignKeys} dispatch={dispatch} canCreate={canCreate} />
        </CardBody>
      </Card>
    );
  }
  if (single.kind === "view") {
    const view = findView(tree, single.id);
    if (!view) return <Alert color="warning">View not found (deleted?).</Alert>;
    // Cap state of the owning camera, so the detail-panel "Duplicate view" button
    // matches the sidebar gating.
    const owningCamera = tree
      .flatMap((d) => d.cameras || [])
      .find((c) => (c.views || []).some((v) => v.public_id === single.id));
    const viewsFull = activeViewCount(owningCamera?.views) >= MAX_VIEWS_PER_CAMERA;
    return (
      <Card>
        <CardBody>
          {fieldError && (
            <Alert color="danger" className="py-2 mb-2" style={{ fontSize: "0.85rem" }}>
              <strong>{fieldError.field || fieldError.code}</strong>: {fieldError.message}
            </Alert>
          )}
          <ViewForm
            view={view}
            foreignKeys={foreignKeys}
            dispatch={dispatch}
            viewsFull={viewsFull}
            canCreate={canCreate}
            lastActiveView={isLastActiveView(owningCamera, view.public_id)}
          />
        </CardBody>
      </Card>
    );
  }
  return null;
}

DetailPanel.propTypes = {
  /** Whether the user may create entities — the `editorRole` gate carried by the legacy recorders. */
  canCreate: PropTypes.bool.isRequired,
  selected: selectedShape.isRequired,
  tree: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string.isRequired })).isRequired,
  store: storeShape,
  foreignKeys: PropTypes.shape({
    brands: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  lastError: PropTypes.shape({
    publicId: PropTypes.string,
    field: PropTypes.string,
    code: PropTypes.string,
    message: PropTypes.string,
  }),
  servers: serversShape,
  machineCache: machineCacheShape.isRequired,
  bulkSelection: PropTypes.shape({
    kind: PropTypes.oneOf(["device", "camera", "view"]).isRequired,
    entities: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string })).isRequired,
    serverEntities: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string })).isRequired,
  }),
};

DetailPanel.defaultProps = { store: null, lastError: null, servers: null, bulkSelection: null };

export default function TreePrototype({ store, servers }) {
  const storeId = store?.id;
  const { tree, serverTree, loading, error, saving, lastError, dispatch, reset, saveAll } = useStoreCameraTree(storeId);
  const foreignKeys = useForeignKeys();
  const [selected, setSelected] = useState({ kind: null, id: null });
  const [expanded, setExpanded] = useState(() => new Set());
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // Bulk mode is explicit: with it off the tree renders exactly as before, which
  // keeps the common single-entity edit uncluttered (device rows already carry the
  // DVR dropdown). `kind` is fixed by the first checked node and released when the
  // selection empties.
  const [bulk, setBulk] = useState({ active: false, kind: null, ids: new Set() });
  // User-chosen size for "Select first N" (kept as text so the field can be emptied while typing).
  const [selectCount, setSelectCount] = useState("20");
  // Same permission boundary as the legacy recorders this tree replaces: creation is
  // reserved to editors (`VideoRecorderList` gates "Add video recorder", `ChannelsTable`
  // gates "Add new channel"), while inspecting and editing what already exists is not
  // role-gated in the UI. Until the tab became visible to non-admins this component had
  // no gate at all, because the admin-only tab was the gate.
  const canCreate = userHasRole(editorRole);

  const statusMap = useMemo(() => computeStatusMap(serverTree, tree), [serverTree, tree]);
  const summary = useMemo(() => summarizeStatus(statusMap), [statusMap]);
  const machineCache = useInferenceMachineCache(tree);

  // Filtering operates on the raw tree (it reads is_active / inference_machine /
  // public_id), independent of the async machineCache. Selection and the detail
  // panel keep using the FULL tree so a node stays inspectable after it's filtered out.
  const filterActive = isFilterActive(filters);
  // alert_channel → segment key, resolved from the FK table (the tree only carries
  // the channel id). Passed explicitly into the pure filter so it stays deterministic.
  const segmentByChannelId = useMemo(() => {
    const map = {};
    (foreignKeys.alertChannels || []).forEach((ch) => {
      map[ch.id] = ch.segment;
    });
    return map;
  }, [foreignKeys.alertChannels]);
  const {
    tree: filteredTree,
    expandIds,
    matchCount,
  } = useMemo(() => filterTree(tree, filters, { segmentByChannelId }), [tree, filters, segmentByChannelId]);
  const machineOptions = useMemo(
    () => collectMachineOptions(tree).map((id) => ({ id, label: machineCache[id] || `#${id}` })),
    [tree, machineCache]
  );
  const segmentOptions = useMemo(() => {
    const labelByKey = {};
    (foreignKeys.segments || []).forEach((s) => {
      labelByKey[s.key] = s.name || s.key;
    });
    return collectSegmentOptions(tree, segmentByChannelId).map((key) => ({ key, label: labelByKey[key] || key }));
  }, [tree, segmentByChannelId, foreignKeys.segments]);
  // When a filter is active, expansion is driven by the matched branches; otherwise
  // it follows the user's manual expand/collapse state in `expanded`. Manual toggles
  // are inert while a filter is active (the rendered set is filter-derived), so the
  // `expanded` set may drift; it is the source of truth again once the filter clears.
  const effectiveExpanded = filterActive ? expandIds : expanded;

  const bodyById = useMemo(
    () => new Map((foreignKeys.bodySensitivities || []).map((s) => [s.id, s.level])),
    [foreignKeys.bodySensitivities]
  );
  const bagById = useMemo(
    () => new Map((foreignKeys.bagSensitivities || []).map((s) => [s.id, s.level])),
    [foreignKeys.bagSensitivities]
  );

  // View count per camera, derived from the UNFILTERED tree, so cap state stays
  // correct even when a filter hides some of a camera's views.
  const viewCountByCamera = useMemo(() => {
    const counts = new Map();
    (tree || []).forEach((device) => {
      (device.cameras || []).forEach((camera) => {
        counts.set(camera.public_id, activeViewCount(camera.views));
      });
    });
    return counts;
  }, [tree]);

  const nodes = useMemo(
    () => buildTreeNodes(filteredTree, statusMap, machineCache, bodyById, bagById, viewCountByCamera),
    [filteredTree, statusMap, machineCache, bodyById, bagById, viewCountByCamera]
  );

  // The entities a bulk edit will reach, resolved against the FULL tree so a node
  // hidden by a filter stays in the selection, and dropping any marked for deletion
  // (they are on their way out; patching them would emit a pointless update op).
  const bulkSelection = useMemo(() => {
    if (!bulk.active || !bulk.kind) return null;
    const pick = (source) =>
      collectOfKind(source, bulk.kind).filter((entity) => bulk.ids.has(entity.public_id) && !entity.localDeleted);
    return { kind: bulk.kind, entities: pick(tree), serverEntities: pick(serverTree) };
  }, [bulk, tree, serverTree]);

  // The kind lock follows the entities a bulk edit can actually reach, not the raw
  // id set: deleting every selected node must release it, or the other two kinds
  // stay disabled with no way to empty the selection.
  const treeBulk = useMemo(
    () => ({
      active: bulk.active,
      kind: bulkSelection && bulkSelection.entities.length > 0 ? bulk.kind : null,
      ids: bulk.ids,
    }),
    [bulk, bulkSelection]
  );

  const onToggleBulkMode = () =>
    // Leaving the mode clears the selection but NOT the draft: edits already made
    // stay pending until Save All.
    setBulk((prev) => (prev.active ? { active: false, kind: null, ids: new Set() } : { ...prev, active: true }));

  const onToggleBulkSelect = (node) => {
    // Clicking a checkbox also moves the detail panel to that node, so a selection
    // of one resolves through `selected` and a later row click can move away.
    setSelected({ kind: node.kind, id: node.id });
    setBulk((prev) => {
      // Derived from `prev` alone, never from a render-time closure, so the updater
      // stays pure: once every selected entity is gone (all deleted), the next
      // checked node starts a fresh selection of its own kind rather than joining a
      // stale, unmatchable one.
      if (effectiveSelection(prev, tree).length === 0) {
        return { active: true, kind: node.kind, ids: new Set([node.id]) };
      }
      const ids = new Set(prev.ids);
      if (ids.has(node.id)) ids.delete(node.id);
      else ids.add(node.id);
      return { active: true, kind: ids.size === 0 ? null : prev.kind, ids };
    });
  };

  // "Select all" acts on what the tree currently shows, so an active filter is the
  // way to grab cameras across several devices without expanding each one.
  const onSelectAllVisible = () =>
    setBulk((prev) => {
      // Same rule as the checkboxes, and same purity: the kind and the starting id
      // set both come from `prev`.
      if (effectiveSelection(prev, tree).length === 0) return prev;
      const ids = new Set(prev.ids);
      const walk = (list) =>
        list.forEach((node) => {
          if (node.kind === prev.kind && !isTemp(node.id) && (statusMap.get(node.id) || "pristine") !== "deleted")
            ids.add(node.id);
          if (node.children) walk(node.children);
        });
      walk(nodes);
      return { ...prev, ids };
    });

  // "Select first N": REPLACES the selection with the first N visible nodes of
  // the current kind, in tree order — the way a "take the 20 first cameras of
  // this machine" workflow wants it (filter first, then cap). Additive would
  // silently grow past N on a second click.
  const onSelectFirstN = (count) =>
    setBulk((prev) => {
      if (!prev.kind || effectiveSelection(prev, tree).length === 0 || !(count > 0)) return prev;
      const ids = new Set();
      const walk = (list) =>
        list.forEach((node) => {
          if (
            ids.size < count &&
            node.kind === prev.kind &&
            !isTemp(node.id) &&
            (statusMap.get(node.id) || "pristine") !== "deleted"
          )
            ids.add(node.id);
          if (node.children) walk(node.children);
        });
      walk(nodes);
      return { ...prev, ids };
    });

  const onAddDevice = () => {
    const id = tempPublicId("cd");
    const partial = { public_id: id };
    if (storeId) partial.store = storeId;
    dispatch({ type: "ADD_DEVICE", partial });
    // A freshly added node (empty name, TEMP id) would be pruned by an active
    // filter and become invisible — clear the filter so it's guaranteed to show.
    setFilters(EMPTY_FILTERS);
    setSelected({ kind: "device", id });
    setExpanded((prev) => new Set(prev).add(id));
  };

  const onSelect = (node) => setSelected({ kind: node.kind, id: node.id });
  const onToggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onAction = (node, action) => {
    if (action === "add") {
      // Clear the filter first: the new child (empty fields, TEMP id) would
      // otherwise be pruned out and invisible while a filter is active.
      setFilters(EMPTY_FILTERS);
      if (node.kind === "device") {
        dispatch({ type: "ADD_CAMERA", deviceId: node.id, partial: {} });
        setExpanded((prev) => new Set(prev).add(node.id));
      } else if (node.kind === "camera") {
        dispatch({ type: "ADD_VIEW", cameraId: node.id, partial: {} });
        setExpanded((prev) => new Set(prev).add(node.id));
      }
      return;
    }
    if (action === "duplicate") {
      // Same reason: the duplicate gets a fresh TEMP id and may not match the
      // active filter — clear it so the copy is visible in the tree.
      setFilters(EMPTY_FILTERS);
      if (node.kind === "device") dispatch({ type: "DUPLICATE_DEVICE", publicId: node.id });
      else if (node.kind === "camera") dispatch({ type: "DUPLICATE_CAMERA", publicId: node.id });
      else if (node.kind === "view") dispatch({ type: "DUPLICATE_VIEW", publicId: node.id });
      return;
    }
    if (action === "delete") {
      if (node.kind === "device") dispatch({ type: "DELETE_DEVICE", publicId: node.id });
      else if (node.kind === "camera") dispatch({ type: "DELETE_CAMERA", publicId: node.id });
      else if (node.kind === "view") dispatch({ type: "DELETE_VIEW", publicId: node.id });
    }
  };

  const onSaveAll = async () => {
    const result = await saveAll();
    if (result?.noop) {
      toast.info("No changes to save.");
      return;
    }
    if (result?.ok) {
      toast.success("All changes saved.");
      return;
    }
    if (result?.refreshed) {
      // The endpoint reports the first stale op, so exactly one entity is named
      // here — on a bulk save the user cannot otherwise tell which one it was.
      // The selection is intentionally left in place so the edit can be re-applied
      // without re-checking every node.
      const label = result.publicId ? describeEntity(tree, result.publicId) : null;
      toast.warn(
        label
          ? `${label} was changed by someone else. Its server version was refreshed — review and Save again.`
          : "One entity was changed by someone else. Server version refreshed — re-apply and Save again."
      );
      return;
    }
    if (result?.error) {
      toast.error(`Save failed: ${result.error.message || result.error.code || "unknown error"}`);
    }
  };

  if (loading) return <Spinner color="primary" />;
  if (error) {
    return (
      <Alert color="danger">
        Failed to load camera model: {error.message || String(error)}.{" "}
        <small>(Is the manager-api /api/camera-model/ endpoint live?)</small>
      </Alert>
    );
  }

  const hasChanges = summary.total > 0;
  const bannerStyle = hasChanges ? undefined : { background: "#23272f", color: "#c8cbd0", border: "1px solid #3a3f4b" };

  return (
    <div>
      <Alert
        color={hasChanges ? "warning" : "secondary"}
        className="mb-3 d-flex justify-content-between align-items-center"
        style={bannerStyle}
      >
        <span>
          {hasChanges ? (
            <>
              <strong>{summary.total}</strong> pending change{summary.total > 1 ? "s" : ""} ·{" "}
              {summary.modified > 0 && <span>✏️ {summary.modified} modified </span>}
              {summary.created > 0 && <span>🟢 {summary.created} new </span>}
              {summary.deleted > 0 && <span>🔴 {summary.deleted} to delete</span>}
            </>
          ) : (
            <span>No pending changes.</span>
          )}
        </span>
        <div className="d-flex" style={{ gap: 8 }}>
          <Button size="sm" outline color="light" onClick={reset} disabled={saving || !hasChanges}>
            Discard
          </Button>
          <Button size="sm" color="primary" onClick={onSaveAll} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <Spinner size="sm" /> Saving…
              </>
            ) : (
              <>💾 Save All ({summary.total})</>
            )}
          </Button>
        </div>
      </Alert>

      {lastError && (
        <Alert color="danger" className="py-2" style={{ fontSize: "0.85rem" }}>
          <strong>Save failed</strong> — {lastError.code}
          {lastError.field ? ` on field "${lastError.field}"` : ""}: {lastError.message}
          {lastError.publicId && (
            <>
              {" "}
              <Button
                size="sm"
                color="link"
                className="p-0 align-baseline"
                onClick={() => {
                  const kind = entityKindFromId(lastError.publicId);
                  if (kind) setSelected({ kind, id: lastError.publicId });
                }}
              >
                Go to entity
              </Button>
            </>
          )}
        </Alert>
      )}

      <Row>
        <Col md="5">
          <Card style={{ minHeight: 600 }}>
            <CardBody style={{ padding: 8 }}>
              <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                <strong>
                  Store hierarchy ({tree.length} device{tree.length > 1 ? "s" : ""})
                </strong>
                <div className="d-flex align-items-center" style={{ gap: 6 }}>
                  <Button size="sm" outline={!bulk.active} color="secondary" onClick={onToggleBulkMode}>
                    ☑ Multiple selection
                  </Button>
                  {bulk.active && (
                    <>
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        onClick={onSelectAllVisible}
                        disabled={!treeBulk.kind}
                        title={
                          treeBulk.kind
                            ? `Select every visible ${treeBulk.kind}`
                            : "Check one node first to pick a kind"
                        }
                      >
                        Select all
                      </Button>
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        onClick={() => onSelectFirstN(Number(selectCount))}
                        disabled={!treeBulk.kind || !(Number(selectCount) > 0)}
                        title={
                          treeBulk.kind
                            ? `Select the first ${selectCount || "N"} visible ${
                                treeBulk.kind
                              }s, replacing the selection`
                            : "Check one node first to pick a kind"
                        }
                        style={{ whiteSpace: "nowrap" }}
                      >
                        Select first
                      </Button>
                      <Input
                        type="number"
                        bsSize="sm"
                        min="1"
                        aria-label="How many to select"
                        value={selectCount}
                        onChange={(e) => setSelectCount(e.target.value)}
                        style={{ width: 64 }}
                      />
                    </>
                  )}
                  {canCreate && (
                    <Button size="sm" color="primary" onClick={onAddDevice}>
                      + Device
                    </Button>
                  )}
                </div>
              </div>
              <TreeFilterBar
                filters={filters}
                machineOptions={machineOptions}
                segmentOptions={segmentOptions}
                matchCount={matchCount}
                filterActive={filterActive}
                onChange={setFilters}
                onClear={() => setFilters(EMPTY_FILTERS)}
              />
              <div
                style={{
                  borderTop: "1px solid #3a3f4b",
                  paddingTop: 8,
                  maxHeight: MAX_TREE_HEIGHT,
                  overflowY: "auto",
                }}
              >
                {nodes.length === 0 ? (
                  <em className="text-muted px-2">
                    {filterActive ? "No entity matches the current filters." : "No capture device yet."}
                  </em>
                ) : (
                  nodes.map((n) => (
                    <TreeNode
                      key={n.id}
                      node={n}
                      depth={0}
                      selectedId={selected.id}
                      expanded={effectiveExpanded}
                      statusMap={statusMap}
                      lastErrorId={lastError?.publicId || null}
                      canCreate={canCreate}
                      bulk={treeBulk}
                      onToggleBulkSelect={onToggleBulkSelect}
                      onSelect={onSelect}
                      onToggle={onToggle}
                      onAction={onAction}
                    />
                  ))
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md="7">
          {foreignKeys.loading ? (
            <Card body>
              <Spinner size="sm" /> <span className="text-muted">Loading dropdowns…</span>
            </Card>
          ) : (
            <DetailPanel
              selected={selected}
              tree={tree}
              store={store}
              foreignKeys={foreignKeys}
              dispatch={dispatch}
              lastError={lastError}
              canCreate={canCreate}
              servers={servers}
              machineCache={machineCache}
              bulkSelection={bulkSelection}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}

TreePrototype.propTypes = {
  store: storeShape,
  servers: serversShape,
};

TreePrototype.defaultProps = { store: null, servers: null };
